import React, { useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { useTranslation } from "../../contexts/I18nProvider";
import DesktopFilterToolbar from "./_components/desktop-filter-toolbar";
import HomeLayout from "../../layouts/home";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../../components/ui/resizable";
import { useSelector } from "react-redux";
import {
  selectSelectedLanguages,
  selectVolume,
} from "../../redux/selectors/filterSelectors";
import LanguageList from "./_components/language-list";
import { Toaster } from "../../components/ui/toaster";
import { TooltipProvider } from "../../components/ui/tooltip";
import { MobileFilterToolbar } from "./_components/mobile-filter-toolbar";
import SentencesSearchInput from "./_components/form-items/sentences-search-input";
import SentencesPerPageInput from "./_components/form-items/sentences-per-page-input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { PolyglotContext } from "./polyglot-context";
import "./variants.css";
import { Badge } from "../../components/ui/badge";
import LanguagesBanner from "./_components/languages-banner";
import ndopPattern from "../../assets/ndop_sample_bamileke.png";

const PolyglotVariant = ({ themeClass, heading, description }) => {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const selectedLanguages = useSelector(selectSelectedLanguages);
  const volume = useSelector(selectVolume);
  const maxItemsPerPage = useSelector(
    (state) => state.filters.maxItemsPerPage
  );

  const playAudio = useCallback(
    (audioPath) => {
      if (!audioPath) {
        console.error("Audio path is missing or undefined.");
        return;
      }

      if (!audioRef.current) {
        console.error("Audio ref is not available.");
        return;
      }

      try {
        const fullAudioPath = `${audioPath}`;
        
        // Reset playback position and stop any current audio
        audioRef.current.currentTime = 0;
        audioRef.current.volume = volume;
        
        // Set the new source
        audioRef.current.src = fullAudioPath;
        
        // Use the canplay event to ensure audio is ready before playing
        const handleCanPlay = () => {
          if (audioRef.current) {
            const playPromise = audioRef.current.play();
            if (playPromise !== undefined) {
              playPromise.catch((error) => {
                if (error.name !== "AbortError") {
                  console.error("Error playing audio:", error.message);
                }
              });
            }
          }
          // Remove the event listener after it fires
          if (audioRef.current) {
            audioRef.current.removeEventListener("canplay", handleCanPlay);
          }
        };
        
        // Add listener for when audio is ready to play
        audioRef.current.addEventListener("canplay", handleCanPlay, { once: true });
        
        // Trigger load of the new source
        audioRef.current.load();
      } catch (error) {
        console.error("Error setting up audio:", error.message);
      }
    },
    [volume]
  );

  const ndopStyle =
    themeClass === "polyglot-theme--ndop"
      ? { "--polyglot-ndop-pattern": `url(${ndopPattern})` }
      : undefined;

  return (
    <TooltipProvider>
      <div className={`polyglot-theme ${themeClass}`} style={ndopStyle}>
        <PolyglotContext.Provider value={{ playAudio, audioRef }}>
        <HomeLayout className="polyglot-page px-2 md:px-6 lg:px-10 2xl:px-12">
          <Toaster />

          <MobileFilterToolbar />
          <ResizablePanelGroup
            direction="horizontal"
            className="lg:space-x-6 polyglot-layout"
          >
            <ResizablePanel
              defaultSize={20}
              minSize={18}
              maxSize={25}
              className="hidden lg:grid pl-2 pr-6 min-w-0 polyglot-sidebar"
            >
              <DesktopFilterToolbar />
            </ResizablePanel>
            <ResizableHandle className="hidden lg:flex" withHandle />
            <ResizablePanel className="relative polyglot-main-panel">
              <div className="flex h-full flex-col gap-6 w-full polyglot-main">
                <audio ref={audioRef} style={{ display: "none" }} />
                <LanguagesBanner />
                <Card className="polyglot-search-card">
                  <CardHeader className="flex flex-row items-center justify-between pb-3">
                    <CardTitle className="polyglot-charis flex items-center gap-2 text-base font-semibold text-primary">
                      <Search className="size-4" />
                      {t('polyglot.searchSentences')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <SentencesSearchInput variant="toolbar" />
                      <SentencesPerPageInput variant="inline" />
                    </div>
                  </CardContent>
                </Card>
                <LanguageList />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </HomeLayout>
        </PolyglotContext.Provider>
      </div>
    </TooltipProvider>
  );
};

export const PolyglotLegacy = () => {
  const { t } = useTranslation();
  return (
    <PolyglotVariant
      themeClass="polyglot-theme--legacy"
      heading={t('polyglot.legacy')}
      description={t('polyglot.legacyDescription')}
    />
  );
};

export const PolyglotNdop = () => {
  const { t } = useTranslation();
  return (
    <PolyglotVariant
      themeClass="polyglot-theme--ndop"
      heading={t('polyglot.ndop')}
      description={t('polyglot.ndopDescription')}
    />
  );
};

export const PolyglotThemeChooser = () => {
  const { t } = useTranslation();
  return (
    <HomeLayout className="polyglot-page">
      <div className="flex flex-col gap-6">
        <div className="polyglot-hero">
          <div>
            <p className="polyglot-eyebrow">{t('polyglot.polyglotThemes')}</p>
            <h2>{t('polyglot.chooseInterface')}</h2>
            <p className="polyglot-subtitle">
              {t('polyglot.previewThemes')}
            </p>
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="polyglot-search-card">
            <CardHeader>
              <CardTitle>{t('polyglot.legacy')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {t('polyglot.classicPalette')}
              </p>
              <Link className="mt-4 inline-flex text-sm text-primary" to="/polyglot-legacy">
                {t('polyglot.openLegacy')}
              </Link>
            </CardContent>
          </Card>
          <Card className="polyglot-search-card">
            <CardHeader>
              <CardTitle>{t('polyglot.ndop')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {t('polyglot.ndopPattern')}
              </p>
              <Link className="mt-4 inline-flex text-sm text-primary" to="/polyglot-ndop">
                {t('polyglot.openNdop')}
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </HomeLayout>
  );
};
